import IconButton from 'components/common/iconButton/IconButton'
import Tag from 'components/common/tags/Tag'
import TermsOfUseAcceptance from 'components/TermsOfUseAcceptance'
import { resizeImageDisplay } from 'components/utils'
import { CustomColorsRef, IconSizes } from 'types/general'

import { FormValues, StepName, WizardStepChangeEventHandler } from '../types'
import ReviewLine from './ReviewLine'

interface ReviewProps {
  stepsName: StepName[]
  formValues: FormValues
  label: string
  onChange: (value: WizardStepChangeEventHandler) => void
  setIsValid: (value: boolean) => void
}

const WizardReview = ({
  stepsName,
  label,
  formValues,
  onChange,
  setIsValid,
}: ReviewProps) => {
  const imageContainer = document.getElementById(
    'review-image-container'
  )?.clientWidth

  const handleDelete = (stepIndex: number, imageIndex: number) => {
    const updatedValues = { ...formValues }
    const key = stepsName[stepIndex].key

    if (Array.isArray(updatedValues[key])) {
      const updatedImages = (updatedValues[key] as File[]).filter(
        (_, i) => i !== imageIndex
      )

      updatedValues[key] = updatedImages

      onChange({
        name: key,
        value: updatedImages,
      })
    }
  }

  if (!stepsName) return null

  return (
    <div>
      <label
        htmlFor="email"
        className="block text-imovSecondaryDark font-bold text-lg mb-2 p-2"
      >
        {label}
      </label>
      <ul className="divide-y overflow-hidden">
        {stepsName.map((stepName, index) => {
          const value = formValues[stepName.key]

          if (value === undefined) return null

          if (stepName.key === 'images' && Array.isArray(value)) {
            return (
              <ReviewLine
                key={`${stepName.value}-${index}`}
                index={index}
                value={stepName.value}
              >
                <div
                  id="review-image-container"
                  className="text-imovPrimary w-full text-right py-2"
                >
                  <div className="flex flex-wrap gap-1 md:gap-3 w-full justify-center">
                    {value.map((image, imageIndex) => {
                      return (
                        <div key={imageIndex} className="relative">
                          <img
                            src={image as unknown as string}
                            alt={`Preview ${imageIndex + 1}`}
                            style={{
                              width: resizeImageDisplay(imageContainer).width,
                              height: resizeImageDisplay(imageContainer).height,
                            }}
                            className="object-cover border border-gray-300 rounded-md shadow-lg"
                            onLoad={() => image}
                          />
                          <IconButton
                            icon="regular-x"
                            size={IconSizes.SM}
                            handleClick={() => handleDelete(index, imageIndex)}
                            color={CustomColorsRef.Gray}
                            hoverColor={CustomColorsRef.Secondary}
                            className="absolute top-1 right-1 rounded-full"
                            aria-label={`Delete image ${imageIndex + 1}`}
                          />
                        </div>
                      )
                    })}
                  </div>
                </div>
              </ReviewLine>
            )
          }

          if (value === 'true' || value === 'false') {
            return (
              <ReviewLine
                key={`${stepName.value}-${index}`}
                index={index}
                value={stepName.value}
              >
                <span className="text-imovPrimary w-full md:text-right md:w-3/4 pb-2 md:py-2 md:ml-4 text-lg">
                  {value ? 'Yes' : 'No'}
                </span>
              </ReviewLine>
            )
          }

          if (
            typeof value === 'object' &&
            value !== null &&
            'formatted_address' in value
          ) {
            return (
              <ReviewLine
                key={`${stepName.value}-${index}`}
                index={index}
                value={stepName.value}
              >
                <span className="text-imovPrimary w-full pb-2 md:py-2 md:ml-4 text-lg">
                  {String(value.formatted_address)}
                </span>
              </ReviewLine>
            )
          }

          if (typeof value === 'object' && Object.keys(value).length > 0) {
            const answerList = Object.keys(value)

            return (
              <ReviewLine
                key={`${stepName.value}-${index}`}
                index={index}
                value={stepName.value}
              >
                <div className="flex flex-wrap capitalize text-left mb-1">
                  {answerList.map((label, index) => {
                    const parsedLabel = label.replace(
                      /([a-z])([A-Z])/g,
                      '$1 $2'
                    )

                    return (
                      <div
                        key={`${label}-${index}`}
                        className="text-imovPrimary gap-1 py-1 ml-2 text-lg flex-shrink-0"
                      >
                        <Tag
                          key={label + index}
                          label={parsedLabel}
                          isOutlined
                        />
                      </div>
                    )
                  })}
                </div>
              </ReviewLine>
            )
          }

          return (
            <ReviewLine
              key={`${stepName.value}-${index}`}
              index={index}
              value={stepName.value}
            >
              <span className="text-imovPrimary w-full pb-2 md:py-2 md:ml-4 text-lg">
                {String(value)}
              </span>
            </ReviewLine>
          )
        })}
      </ul>
      <ul>
        <TermsOfUseAcceptance
          onChange={onChange}
          formValues={formValues}
          setIsValid={setIsValid}
        />
      </ul>
    </div>
  )
}

export default WizardReview
